import type { GridsterItemComponentInterface } from 'angular-gridster2';

import { OmitDistributive } from '@core/common-legacy';
import type { SortDirection } from '@core/search-types';

import { ReportDateFilter } from './common.types';
import {
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewMode,
  ReportDashboardUnavailabilityReason,
} from './consts.types';
import { TimeBasedMetricDatePoint } from './metrics.types';

export interface ReportDashboardComponentGridPosition {
  cols: number;
  rows: number;
  x: number;
  y: number;
}

export type ReportDashboardGridComponent = Pick<
  GridsterItemComponentInterface,
  '$item' | 'notPlaced'
>;

export interface ReportDashboardComponentSettings {
  interval?: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  amountOfDataDisplayed?: number;
  localSortBy?: string | null;
  localSortOrder?: SortDirection | null;
}

export type ReportDashboardComponentOptionsKeys =
  | 'includeDeletedCandidates'
  | 'includeArchivedJobs'
  | 'showAllData'
  | 'includeArchivedRequisitions';

export type ReportDashboardComponentOptions = {
  [key in ReportDashboardComponentOptionsKeys]?: boolean;
};

export type ReportDashboardComponentOrigin = 'blank' | 'template' | 'duplicate' | null;

export type ReportParamFilter = { group: string; value: string; inverse?: boolean; label?: string };
export type ReportParamFilters = ReportParamFilter[];

export interface ReportDashboardComponentViewDataBase {
  templateId: string | null;
  isAvailable: boolean;
  unavailableReason: null | ReportDashboardUnavailabilityReason;
  name: string;
  description: string | null;
  chartType: ReportDashboardComponentChartType | null;
  metric: string;
  grid: ReportDashboardComponentGridPosition;
  sortBy: string | null;
  sortOrder: SortDirection | null;
  settings?: ReportDashboardComponentSettings;
  filters: ReportParamFilters;
  primaryGroup: string | null;
  secondaryGroup: string | null;
  dateFilter: ReportDateFilter;
  dateField: {
    field: string;
    resource: string;
  };
  options: ReportDashboardComponentOptions;
  viewMode: ReportDashboardComponentViewMode;
  origin: ReportDashboardComponentOrigin;
  generated?: boolean;
}

export interface ReportDashboardComponentViewDataNonTimeBased extends ReportDashboardComponentViewDataBase {
  kind: Exclude<ReportDashboardComponentKind, ReportDashboardComponentKind.TimeBased>;
}

export interface ReportDashboardComponentViewDataTimeBased extends ReportDashboardComponentViewDataBase {
  kind: ReportDashboardComponentKind.TimeBased;
  startPoint: TimeBasedMetricDatePoint['key'] | null;
  endPoint: TimeBasedMetricDatePoint['key'] | null;
}

export type ReportDashboardComponentViewData =
  | ReportDashboardComponentViewDataNonTimeBased
  | ReportDashboardComponentViewDataTimeBased;

export type ReportDashboardComponent = ReportDashboardComponentViewData & {
  id: number;
  localId: string;
};

export enum ReportComponentActionIntent {
  Edit = 'edit',
  Fullscreen = 'fullscreen',
  Duplicate = 'duplicate',
  Move = 'move',
  Delete = 'delete',
  ExportCsv = 'exportCsv',
  ExportPng = 'exportPng',
  ExportXlsx = 'exportXlsx',
  ExportPdf = 'exportPdf',
}

export const REPORT_COMPONENT_DRAG_HANDLE_CLASS = 'drag-handle';

// think about renaming, this is just a component without id
// And it's used in builder when creating and when editing
export type ReportDashboardComponentCreate = OmitDistributive<ReportDashboardComponent, 'id'>;

export type ReportDashboardComponentRequest = OmitDistributive<
  ReportDashboardComponent,
  ReportDashboardComponentOptionsKeys
> & {
  options: ReportDashboardComponentOptions;
};

export type ReportDashboardComponentCreateRequest = OmitDistributive<
  ReportDashboardComponentRequest,
  'id'
>;

export type ReportCreateComponentActionPayload = {
  data: {
    dashboardId: number;
    component: ReportDashboardComponentCreate;
  };
  correlationId: string;
};

export type ReportCreateManyComponentActionPayload = {
  dashboardId: number;
  componentsData: Array<{
    component: ReportDashboardComponentCreate;
    correlationId: string;
  }>;
};

export type ReportComponentInfoProps = Pick<ReportDashboardComponent, 'name' | 'description'>;

export type ReportComponentVisualizationProps = Pick<
  ReportDashboardComponent,
  'viewMode' | 'chartType'
> & {
  settings?: Pick<
    ReportDashboardComponentSettings,
    'amountOfDataDisplayed' | 'localSortBy' | 'localSortOrder'
  >;
};

export type ReportComponentGridProps = ReportDashboardComponent['grid'];

export type ReportComponentGridPropsDecorated = ReportComponentGridProps & {
  minItemRows?: number;
  maxItemRows?: number;
  minItemCols?: number;
};

type DynamicKeysBase =
  | 'metric'
  | 'options'
  | 'primaryGroup'
  | 'secondaryGroup'
  | 'sortBy'
  | 'sortOrder'
  | 'filters'
  | 'dateFilter'
  | 'dateField'
  | 'kind';

export type ReportComponentDataProps = {
  settings?: Pick<ReportDashboardComponentSettings, 'interval'>;
} & (
  | Pick<ReportDashboardComponentViewDataNonTimeBased, DynamicKeysBase>
  | Pick<ReportDashboardComponentViewDataTimeBased, DynamicKeysBase | 'startPoint' | 'endPoint'>
);

type ReportComponentDynamicProps = ReportComponentDataProps &
  ReportComponentInfoProps &
  ReportComponentVisualizationProps & { grid: ReportComponentGridProps };

export type ReportComponentStaticProps = Omit<
  ReportDashboardComponent,
  keyof ReportComponentDynamicProps
>;
